import {AssetModel, AssetSelectedModel} from '../_models/asset.model';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {catchError, takeUntil} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ErrorService} from '../../shared/_services/error.service';
import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {LoadingService} from '../../shared/_services/loading.service';
import {SbService} from '../../shared/_services/sb.service';
import {DateService} from './date.service';

@Injectable({
  providedIn: 'root'
})
export class AssetsService implements OnDestroy {

  private assets$ = new BehaviorSubject<AssetModel[]>([]);

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private dateService: DateService,
    private errorService: ErrorService,
    private httpClient: HttpClient,
    private loadingService: LoadingService,
    private sbService: SbService,
  ) {
    this.getDate();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getDate(): void {
    // just to be sure we have a date before anything else
    this.dateService.onNewDate()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (date) => {
          if (date && !this.getAssets().length) {
            this.callGetAssets();
          }
        }
      );
  }

  callGetAssets(): void {
    const loadMsg = 'loading list of available assets';
    this.loadingService.start(loadMsg);
    this.getAssetsHttp()
      .subscribe(
        (data) => {
          this.setAssets(data);
          this.loadingService.stop(loadMsg);
        },
        (err) => {
          this.newAssets([]);
          this.loadingService.stop(loadMsg);
          this.sbService.message('Something went wrong loading the list of assets: ' + err.errMessage);
        }
      );
  }

  private getAssetsHttp(): Observable<any> {
    return this.httpClient.get(environment.urlWarp + '/asset').pipe(
      catchError(this.errorService.handleError)
    );
  }

  private setAssets(data: any): void {
    const assets = [];
    if (data?.data?.length) {
      for (const country of data.data) {
        assets.push(new AssetModel(country));
      }
    }
    this.newAssets(assets);
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  setAssetsSelected(country: AssetModel): void {
    country.assetsSelected = [];
    // no recursive iteration here, only the bottom level counts as real selection, so easier this way
    for (const cust of country.customers) {
      for (const cat of cust.cats) {
        for (const asset of cat.assets) {
          if (asset.selected) {
            country.assetsSelected.push(asset.label);
          }
        }
      }
    }
  }

  // ********************************************************************************************************
  // BROADCAST DATA
  // ********************************************************************************************************

  newAssets(data: AssetModel[]) {
    this.assets$.next(data);
  }

  onNewAssets(): Observable<AssetModel[]> {
    return this.assets$.asObservable();
  }

  getAssets(): AssetModel[] {
    return this.assets$.getValue();
  }

  getSelAssets(): AssetSelectedModel[] {
    const assets = this.getAssets();
    const sels: AssetSelectedModel[] = [];
    for (const country of assets) {
      for (const cust of country.customers) {
        for (const cat of cust.cats) {
          for (const asset of cat.assets) {
            if (asset.selected) {
              sels.push({asset: asset.id, assetLabel: asset.label, country: country.label});
            }
          }
        }
      }
    }
    return sels;
  }

}
